import React, { useEffect, useState } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import MaintenancePage from "./components/pages/MaintenancePage";
import Layout from "./components/nav/Layout";
import Accessibility from "./components/nav/Accessibility";
import ContactUsPage from "./components/pages/ContactUs";
import ArrangementPage from "./components/pages/Arrangement";
import SettlementPage from "./components/pages/Settlement";
import ErrorPage from "./components/pages/Error";
import NotFoundPage from "./components/pages/NotFound";
import IdvAuthPage from "./components/pages/IdvAuth";
import IdvOtpAuthPage from "./components/pages/IdvOtpAuth";
import RegOtpAuthPage from "./components/pages/RegOtpAuthPage";
import AccountPage from "./components/pages/Account";
import PaymentPage from "./components/pages/Payment";
import PaymentConfirmation from "./components/payment/PaymentConfirmation";
import ScrollToTop from "./components/nav/ScrollToTop";
import environment from "./env";
import Profile from "./components/pages/Profile";
import CallMeDialog from "./components/pages/CallMeDialog";
import { useClient } from "./context/ClientProvider";
import { useNav } from "./context/NavProvider";
import SettlementProvider from "./context/SettlementProvider";
import TransactionHistoryProvider from "./context/TransactionHistoryProvider";
import PaymentProvider from "./context/PaymentProvider";
import CallMeProvider from "./context/CallMeProvider";
import { AuthGuard } from "./components/auth/AuthGuard";
import CookieProvider, { useCookie } from "./context/CookieProvider";
import CookieConsentDialog from "./components/account/CookieConsentDialog";
import TransactionHistory from "./components/pages/TransactionHistory";
import CookiePolicy from "./components/pages/CookiePolicy";
import ManageCards from "./components/pages/ManageCards";
import ProfileProvider from "./context/ProfileProvider";
import CommunicationPreferences from "./components/profile/CommunicationPreferences";
import ContactPreferences from "./components/profile/ContactPreferences";
import CallMeDialogLogin from "./components/pages/CallMeDialogLogin";
import NotificationsPage from "./components/pages/Notifications";
import WaysToPay from "./components/pages/WaysToPay";
import UsefulTips from "./components/pages/UsefulTips";
import DocumentsPage from "./components/pages/Documents";
import FinancialStatement from "./features/financial-statement/pages/FinancialStatement";
import FinancialSteps from "./features/financial-statement/components/FinancialSteps";
import { SfsOutlet } from "./features/financial-statement/pages/SfsOutlet";

if (process.env.NODE_ENV === 'production') {
  console.log = () => { };
  console.info = () => { };
  console.warn = () => { };
  console.error = () => { };
}

function App() {

  const {
    useGetConfig,
    settlementConfig,
    maintenanceMode,
    setMaintenanceMode
  } = useClient();
  const { clientConfig, isLoading } = useGetConfig(environment.CLIENT_NAME);

  const unauthPages = clientConfig?.layout?.unauthenticatedPages || {};
  const contactUsDataPerClient = unauthPages.contactUs;
  const waysToPayDataPerClient = unauthPages.findWaysToPay;
  const usefulTipsDataPerClient = unauthPages.usefulTips;

  const { setShowFooter } = useNav();
  const { getCookie, setCookie } = useCookie();
  const [open, setOpen] = useState(false);
  const [openCall, setOpenCall] = useState(false);
  const [openCallDialogLogin, setOpenCallDialogLogin] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const isCookiePolicyPage = window.location.pathname.toLowerCase() === "/cookiepolicy";

  useEffect(() => {
    const consent = getCookie("google_analytics");
    if (consent === "accepted") {
      setConsentGiven(true);
  } else if (!consent && !isCookiePolicyPage) {
    setOpen(true);
    }
}, [getCookie]);

  useEffect(() => {
    setShowFooter(true);
  }, [setShowFooter]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseCall = () => {
    setOpenCall(false);
  };

  const handleAccept = () => {
    setCookie("google_analytics", "accepted", 365, "/");
    setConsentGiven(true);
    handleClose();
  };

  const handleDecline = () => {
    setCookie("google_analytics", "declined", 365, "/");
    handleClose();
  };


  useEffect(() => {
    if (clientConfig) {
      setMaintenanceMode(clientConfig?.maintenance?.active);
    }
  }, [clientConfig]);

  console.log("🍪 Consent given?", consentGiven);


  const getAuthPageComponent = (authType) => {
    switch (authType) {
      case "IDV":
        return <IdvAuthPage />;
      case "IDV-OTP":
        return <IdvOtpAuthPage />;
      case "REG-OTP":
        return <RegOtpAuthPage />;
      default:
        break;
    }
  }

  const unauthenticatedRoutes = [];
  console.log("Contact Us Enabled:", contactUsDataPerClient?.enabled);
  console.log("Ways to Pay Enabled:", waysToPayDataPerClient?.enabled);
  console.log("Useful Tips Enabled:", usefulTipsDataPerClient?.enabled);

  // Conditionally add Contact Us
  if (contactUsDataPerClient?.enabled) {
    unauthenticatedRoutes.push({
      path: "contact-us",
      element: <ContactUsPage />
    });
  }

  // Conditionally add Ways to Pay
  if (waysToPayDataPerClient?.enabled) {
    unauthenticatedRoutes.push({
      path: "ways-to-pay",
      element: <WaysToPay />
    });
  }

  // Conditionally add Useful Tips
  if (usefulTipsDataPerClient?.enabled) {
    unauthenticatedRoutes.push({
      path: "useful-tips",
      element: <UsefulTips />
    });
  }

  if (!isLoading && clientConfig) {
    const routerData = [
      {
        path: "/",
        element: <Layout />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <AccountPage />
              </AuthGuard>
            ),
          },
          {
            path: "auth",
            element: getAuthPageComponent(clientConfig?.layout?.auth?.authType)
          },
          { path: "account", element: <AccountPage /> },
          {
            path: "Accessibility",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <Accessibility />
              </AuthGuard>
            ),
          },
          ...unauthenticatedRoutes,
          {
            path: "payment",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <PaymentProvider>
                  <PaymentPage />
                </PaymentProvider>
              </AuthGuard>
            ),
          },
            {
                path: "sfs",
                element: <SfsOutlet />,
                children: [
                    { index: true, element: <FinancialStatement /> },
                    { path: ":step", element: <FinancialSteps /> },
                ],
            },
          {
            path: "cookiepolicy",
            element: (
              <>
                <ScrollToTop />
                <CookiePolicy />
              </>
            ),
          },
          {
            path: "transaction-history",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <TransactionHistoryProvider>
                  <TransactionHistory />
                </TransactionHistoryProvider>
              </AuthGuard>
            ),
          },
          {
            path: "manage-cards",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <PaymentProvider>
                  <ManageCards />
                </PaymentProvider>
              </AuthGuard>
            ),
          },
          {
            path: "profile",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <ProfileProvider>
                  <Profile />
                </ProfileProvider>
              </AuthGuard>
            ),
          },
          {
            path: "ContactPreferences",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <ProfileProvider>
                  <ContactPreferences />
                </ProfileProvider>
              </AuthGuard>
            ),
          },
          {
            path: "CommunicationPreferences",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <ProfileProvider>
                  <CommunicationPreferences />
                </ProfileProvider>
              </AuthGuard>
            ),
          },
          {
            path: "CallMe",
            element: (
              <CallMeDialog open={openCall} onClose={handleCloseCall} />
            ),
          },

          {
            path: "callmelogin", element: (<CallMeProvider><CallMeDialogLogin open={openCallDialogLogin} onClose={() => setOpenCallDialogLogin(false)} />
            </CallMeProvider>)
          },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
      {
        path: "/repayment",
        element: <Layout />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "",
            element: (
              <AuthGuard>
                <ScrollToTop />
                <ArrangementPage />
              </AuthGuard>
            ),
          },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
      {
        path: "/payment/:transactionRef",
        element: (
          <AuthGuard>
            <ScrollToTop />
            <PaymentConfirmation />
          </AuthGuard>
        ),
        loader: ({ params }) => params.transactionRef,
      },
    ];

    if (settlementConfig?.required) {
      routerData[0].children.push({
        path: "settlement/:settlementOption",
        element: (
          <AuthGuard>
            <ScrollToTop />
            <SettlementProvider>
              <SettlementPage />
            </SettlementProvider>
          </AuthGuard>
        ),
        loader: ({ params }) => params.settlementOption,
      });
    }

    if (clientConfig?.layout?.notifications?.showNotifications) {
      routerData[0].children.push({
        path: "notifications",
        element: (
          <AuthGuard>
            <ScrollToTop />
            <NotificationsPage />
          </AuthGuard>
        ),
      });
    }

    if (clientConfig?.layout?.documents?.enabled) {
      routerData[0].children.push({
        path: "documents",
        element: (
          <AuthGuard>
            <ScrollToTop />
            <DocumentsPage />
          </AuthGuard>
        ),
      });
    }
    const router = createBrowserRouter(routerData);

    return maintenanceMode ? (
      <ThemeProvider theme={createTheme(clientConfig?.theme)}>
        <MaintenancePage />
      </ThemeProvider>
    ) : (
      <CookieProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en-gb'>
          <ThemeProvider theme={createTheme(clientConfig?.theme)}>
            <RouterProvider router={router} />
              {!isCookiePolicyPage && (
                <CookieConsentDialog
                  open={open}
                  onClose={handleClose}
                  onAccept={handleAccept}
                  onDecline={handleDecline}
                />
              )}
          </ThemeProvider>
        </LocalizationProvider>
      </CookieProvider>
    );
  }

  return null;
}

export default App;
